import { useState, useEffect, useRef } from 'react';
import { useConsent } from '../contexts/ConsentContext';

// ── CookieBanner ──────────────────────────────────────────────────────────────
//
//  GDPR-compliant cookie consent banner.
//  Appears at bottom of viewport on first visit.  Does not overlay content —
//  the parent must apply padding-bottom equal to the banner height.
//
//  Consent categories:
//    Necessary   — always on (required)
//    Analytics   — Sentry session replays
//    Preferences — UX memory (feed tab, editor tour, font size…)
//
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORIES = [
  {
    id:          'necessary',
    label:       'Necessary',
    description: 'Session authentication, theme preference, and other functional storage required for the app to work.',
    required:    true,
  },
  {
    id:          'analytics',
    label:       'Analytics',
    description: 'Sentry session replays and performance monitoring. Helps us diagnose bugs. Error reporting (without replays) is always active as a legitimate interest.',
    required:    false,
  },
  {
    id:          'preferences',
    label:       'Preferences',
    description: 'Remembers your feed tab, editor tour state, font size, and other UI preferences across sessions.',
    required:    false,
  },
];

const Toggle = ({ on, disabled, onChange }) => (
  <button
    role="switch"
    aria-checked={on}
    disabled={disabled}
    onClick={() => !disabled && onChange(!on)}
    style={{
      position:    'relative',
      width:       40,
      height:      22,
      borderRadius: 11,
      border:      'none',
      cursor:      disabled ? 'not-allowed' : 'pointer',
      background:  on ? 'var(--accent)' : 'var(--border2)',
      transition:  'background 0.2s',
      flexShrink:  0,
      outline:     'none',
      padding:     0,
    }}
  >
    <span style={{
      position:    'absolute',
      top:          3,
      left:        on ? 21 : 3,
      width:        16,
      height:       16,
      borderRadius: '50%',
      background:  '#fff',
      transition:  'left 0.2s',
      display:     'block',
    }} />
  </button>
);

const CookieBanner = ({ onHeightChange }) => {
  const { hasConsented, consent, updateConsent } = useConsent();
  const [expanded,    setExpanded]    = useState(false);
  const [analytics,   setAnalytics]   = useState(false);
  const [preferences, setPreferences] = useState(false);
  const [saved,       setSaved]       = useState(false);
  const bannerRef = useRef(null);
  const firstBtnRef = useRef(null);

  // Move focus to the banner when it appears
  useEffect(() => {
    if (!hasConsented && firstBtnRef.current) {
      firstBtnRef.current.focus();
    }
  }, [hasConsented]);

  // Report banner height so the parent can add padding
  useEffect(() => {
    if (!bannerRef.current || !onHeightChange) return;
    const obs = new ResizeObserver(entries => {
      onHeightChange(entries[0]?.contentRect?.height ?? 0);
    });
    obs.observe(bannerRef.current);
    return () => obs.disconnect();
  }, [onHeightChange]);

  useEffect(() => {
    if (onHeightChange && hasConsented) onHeightChange(0);
  }, [hasConsented, onHeightChange]);

  const dismiss = (analyticsVal, preferencesVal) => {
    updateConsent({ analytics: analyticsVal, preferences: preferencesVal });
    if (onHeightChange) onHeightChange(0);
  };

  const handleAcceptAll = () => dismiss(true, true);

  const handleNecessaryOnly = () => dismiss(false, false);

  const handleSavePreferences = () => {
    dismiss(analytics, preferences);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') handleNecessaryOnly();
  };

  if (hasConsented) return null;

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-label="Cookie consent"
      aria-modal="false"
      onKeyDown={handleKeyDown}
      className="cookie-banner"
    >
      {!expanded ? (
        /* ── Compact bar ── */
        <div className="cookie-banner__bar">
          <p className="cookie-banner__text">
            We use cookies and local storage to keep you signed in and remember your preferences.
            We also use Sentry for error monitoring to improve the app.{' '}
            <a href="/privacy" className="cookie-banner__link">Privacy Policy</a>
            {' · '}
            <button className="cookie-banner__text-btn" onClick={() => setExpanded(true)}>
              Manage Preferences
            </button>
          </p>
          <div className="cookie-banner__actions">
            <button className="btn-ghost btn-sm" onClick={() => setExpanded(true)}>
              Learn more
            </button>
            <button className="btn-ghost btn-sm" onClick={handleNecessaryOnly}>
              Necessary Only
            </button>
            <button ref={firstBtnRef} className="btn-primary btn-sm" onClick={handleAcceptAll}>
              Accept All
            </button>
          </div>
        </div>
      ) : (
        /* ── Detailed panel ── */
        <div className="cookie-banner__panel">
          <div className="cookie-banner__panel-header">
            <h2 className="cookie-banner__title">Cookie Preferences</h2>
            <button className="btn-ghost btn-sm" onClick={() => setExpanded(false)}>✕</button>
          </div>
          <p className="cookie-banner__panel-desc">
            Choose which cookies and local storage Chronos may use.{' '}
            <a href="/privacy" className="cookie-banner__link">Privacy Policy</a>
          </p>

          <div className="cookie-banner__categories">
            {CATEGORIES.map(cat => {
              const value = cat.id === 'analytics'
                ? analytics
                : cat.id === 'preferences'
                  ? preferences
                  : true;
              const setter = cat.id === 'analytics' ? setAnalytics : setPreferences;
              return (
                <div key={cat.id} className="cookie-banner__category">
                  <div className="cookie-banner__category-info">
                    <div className="cookie-banner__category-label">
                      {cat.label}
                      {cat.required && (
                        <span className="cookie-banner__required">Required</span>
                      )}
                    </div>
                    <p className="cookie-banner__category-desc">{cat.description}</p>
                  </div>
                  <Toggle
                    on={value}
                    disabled={cat.required}
                    onChange={setter}
                  />
                </div>
              );
            })}
          </div>

          <div className="cookie-banner__panel-actions">
            <button className="btn-ghost btn-sm" onClick={handleNecessaryOnly}>
              Necessary Only
            </button>
            <button className="btn-ghost btn-sm" onClick={handleAcceptAll}>
              Accept All
            </button>
            <button ref={firstBtnRef} className="btn-primary btn-sm" onClick={handleSavePreferences}>
              {saved ? 'Saved ✓' : 'Save Preferences'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CookieBanner;
